import { useState } from 'react'
import { post } from '../../../../../utils/Api'
import { useGlobalContext } from '../../../../../utils/Context'
import FormLoader from '../../../../../components/loader/FormLoader'
import { Icon } from '@iconify/react'
import { WeightDiscrepancyCheckResult } from '../types'

interface Props {
  onClose: () => void
  onSuccess: () => void
  onRecordDiscrepancy: () => void
}

function CheckDiscrepancyModal({ onClose, onRecordDiscrepancy }: Props) {
  const { notify } = useGlobalContext()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    trackingId: '',
    newWeight: '',
  })
  const [result, setResult] = useState<WeightDiscrepancyCheckResult | null>(
    null,
  )

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleCheck = async () => {
    if (!formData.trackingId.trim()) {
      notify('warn', 'Please enter a tracking ID')
      return
    }
    if (!formData.newWeight || parseFloat(formData.newWeight) <= 0) {
      notify('warn', 'Please enter a valid weight')
      return
    }

    setLoading(true)
    setResult(null)

    try {
      const response = await post(
        'admin/weight-discrepancy/check',
        {
          trackingId: formData.trackingId.trim(),
          newWeight: parseFloat(formData.newWeight),
        },
        null,
      )

      if (response.status === 200) {
        setResult(response.data)
        notify('success', 'Discrepancy check complete')
      } else {
        notify('error', response.message || 'Failed to check discrepancy')
      }
    } catch (error) {
      notify('error', 'Failed to check discrepancy')
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
    }).format(amount)
  }

  return (
    <div className='modal-overlay'>
      <div className='modal-content'>
        <div className='modal-header'>
          <h2>Check Weight Discrepancy</h2>
          <button className='close-btn' onClick={onClose}>
            <Icon icon='mdi:close' />
          </button>
        </div>

        <div className='modal-body'>
          <div className='form-group'>
            <label>Tracking ID</label>
            <input
              type='text'
              name='trackingId'
              value={formData.trackingId}
              onChange={handleChange}
              placeholder='Enter tracking ID (e.g., QC123456789)'
            />
          </div>

          <div className='form-group'>
            <label>New Weight (kg)</label>
            <input
              type='number'
              name='newWeight'
              value={formData.newWeight}
              onChange={handleChange}
              placeholder='Enter the actual weight'
              step='0.1'
              min='0'
            />
          </div>

          {result && (
            <div className='check-result'>
              <h4>Discrepancy Result</h4>

              {result.error || result.message ? (
                <>
                  <div className='result-row'>
                    <span>Tracking ID</span>
                    <span>{result.trackingId}</span>
                  </div>
                  <div className='result-row'>
                    <span>Old Weight</span>
                    <span>{result.oldWeight} kg</span>
                  </div>
                  <div className='result-row'>
                    <span>New Weight</span>
                    <span>{result.newWeight} kg</span>
                  </div>
                  <div className='result-row'>
                    <span>Weight Difference</span>
                    <span>{result.weightDiff} kg</span>
                  </div>
                  <div className='result-row'>
                    <span>Old Price</span>
                    <span>{formatCurrency(result.oldPrice)}</span>
                  </div>
                  <div className='error-message'>
                    <Icon
                      icon='mdi:alert-circle'
                      style={{ marginRight: '8px' }}
                    />
                    {result.message || result.error}
                  </div>
                </>
              ) : (
                <>
                  <div className='result-row'>
                    <span>Tracking ID</span>
                    <span>{result.trackingId}</span>
                  </div>
                  <div className='result-row'>
                    <span>Old Weight</span>
                    <span>{result.oldWeight} kg</span>
                  </div>
                  <div className='result-row'>
                    <span>New Weight</span>
                    <span>{result.newWeight} kg</span>
                  </div>
                  <div className='result-row'>
                    <span>Weight Difference</span>
                    <span>+{result.weightDiff} kg</span>
                  </div>
                  <div className='result-row'>
                    <span>Old Price</span>
                    <span>{formatCurrency(result.oldPrice)}</span>
                  </div>
                  <div className='result-row'>
                    <span>New Price</span>
                    <span>{formatCurrency(result.newPrice || 0)}</span>
                  </div>
                  <div className='result-row highlight'>
                    <span>Suggested Amount</span>
                    <span>{formatCurrency(result.suggestedAmount || 0)}</span>
                  </div>
                </>
              )}
            </div>
          )}

          <div className='modal-actions'>
            <button className='btn-secondary' onClick={onClose}>
              Cancel
            </button>
            {loading ? (
              <FormLoader position='center' />
            ) : (
              <>
                <button className='btn-primary' onClick={handleCheck}>
                  {result ? 'Check Again' : 'Check'}
                </button>
                {result && !result.error && result.suggestedAmount && (
                  <button className='btn-success' onClick={onRecordDiscrepancy}>
                    Record Discrepancy
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default CheckDiscrepancyModal
